import Decimal from "decimal.js";
import { v4 as uuidv4 } from "uuid";
import { layoutOf } from "../utils/selfcheckoutMode";
import {
  CURRENCY,
  fbqTrack,
  pushDataLayer,
  sumItems,
  toMetaContents,
  toNumber,
  toTrackingItem,
} from "../services/tracking";

// O funil de compra para GA4 (dataLayer) e Meta Pixel (fbq), com os eventos
// recomendados de cada um. São thunks para ler o estado na hora: itens,
// grupos e o modo do aparelho.
//
// Totem, tablet e POS (layout.sharedDevice) não medem nada: o aparelho é da
// loja, não um cliente vindo de campanha.
//
// Rastreamento nunca pode quebrar o pedido: todo evento roda em try/catch.

const track = (fn) => (dispatch, getState) => {
  const state = getState();
  if (layoutOf(state.shop).sharedDevice) return;
  try {
    fn(state);
  } catch (error) {
    console.error("Falha ao enviar evento de rastreamento", error);
  }
};

const groupNameOf = (state, product) =>
  (state.products.groups || []).find((g) => g.id === product.groupId)?.name;

const itemOfOrder = (state, order, cost) => {
  const product =
    state.products.items.find((p) => p.id === order.productId) || order.product;
  if (!product) return null;
  return toTrackingItem({
    product,
    qtt: order.qtt,
    cost: cost ?? state.shoppingCart.cachedCostPerOrder[order.uuid],
    groupName: groupNameOf(state, product),
  });
};

const cartItemsOf = (state) =>
  state.shoppingCart.productOrders
    .filter((o) => o.commited)
    .map((o) => itemOfOrder(state, o))
    .filter(Boolean);

/**
 * page_view virtual a cada troca de rota. O Meta Pixel já conta PageView
 * sozinho em pushState/replaceState: disparar aqui duplicaria.
 */
export const trackPageView = () =>
  track(() => {
    pushDataLayer("virtual_page_view", {
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  });

export const trackViewItem = (productId) =>
  track((state) => {
    const product = state.products.items.find((p) => p.id === productId);
    if (!product) return;
    const item = toTrackingItem({
      product,
      groupName: groupNameOf(state, product),
    });
    const eventId = uuidv4();

    pushDataLayer("view_item", {
      event_id: eventId,
      ecommerce: { currency: CURRENCY, value: item.price, items: [item] },
    });
    fbqTrack(
      "ViewContent",
      {
        content_name: product.name,
        content_category: item.item_category,
        value: item.price,
        currency: CURRENCY,
        ...toMetaContents([item]),
      },
      eventId,
    );
  });

export const trackAddToCart = (orderUuid) =>
  track((state) => {
    const order = state.shoppingCart.productOrders.find(
      (o) => o.uuid === orderUuid,
    );
    const item = order && itemOfOrder(state, order);
    if (!item) return;
    const value = sumItems([item]);
    const eventId = uuidv4();

    pushDataLayer("add_to_cart", {
      event_id: eventId,
      ecommerce: { currency: CURRENCY, value, items: [item] },
    });
    fbqTrack(
      "AddToCart",
      {
        content_name: item.item_name,
        value,
        currency: CURRENCY,
        ...toMetaContents([item]),
      },
      eventId,
    );
  });

export const trackViewCart = () =>
  track((state) => {
    const items = cartItemsOf(state);
    if (items.length === 0) return;
    pushDataLayer("view_cart", {
      ecommerce: { currency: CURRENCY, value: sumItems(items), items },
    });
  });

/**
 * O cliente escolheu entrega ou retirada: é o começo do checkout de fato
 * (antes disso ele só olhou a sacola).
 */
export const trackBeginCheckout = (shippingTier) =>
  track((state) => {
    const items = cartItemsOf(state);
    if (items.length === 0) return;
    const value = sumItems(items);
    const eventId = uuidv4();

    pushDataLayer("begin_checkout", {
      event_id: eventId,
      ecommerce: { currency: CURRENCY, value, items },
    });
    pushDataLayer("add_shipping_info", {
      ecommerce: {
        currency: CURRENCY,
        value,
        shipping_tier: shippingTier,
        items,
      },
    });
    fbqTrack(
      "InitiateCheckout",
      { value, currency: CURRENCY, ...toMetaContents(items) },
      eventId,
    );
  });

export const trackPaymentInfo = (paymentName) =>
  track((state) => {
    const items = cartItemsOf(state);
    if (items.length === 0) return;
    const value = sumItems(items);
    const eventId = uuidv4();

    pushDataLayer("add_payment_info", {
      event_id: eventId,
      ecommerce: {
        currency: CURRENCY,
        value,
        payment_type: paymentName,
        items,
      },
    });
    fbqTrack(
      "AddPaymentInfo",
      { value, currency: CURRENCY, ...toMetaContents(items) },
      eventId,
    );
  });

/**
 * Pedido aceito pelo backend. `eventId` é o uuid do envio
 * (submittingData.uuid), o mesmo que vai em `marketing.eventId` para o
 * backend mandar o Purchase pela Conversions API: é por ele que a Meta
 * deduplica navegador e servidor.
 *
 * O `value` do GA4 exclui o frete (vai em `shipping`), como o GA pede; o do
 * Meta é o total pago.
 */
export const trackPurchase = ({
  eventId,
  orderId,
  productOrders,
  costs,
  coupon,
}) =>
  track((state) => {
    const items = productOrders
      .map((o) => itemOfOrder(state, o, o.cachedCost))
      .filter(Boolean);
    const shipping = toNumber(costs.delivery);
    const total = toNumber(costs.total);

    pushDataLayer("purchase", {
      event_id: eventId,
      ecommerce: {
        transaction_id: String(orderId || eventId),
        currency: CURRENCY,
        value: toNumber(Decimal(total).minus(shipping)),
        shipping,
        ...(coupon ? { coupon } : {}),
        items,
      },
    });
    fbqTrack(
      "Purchase",
      {
        value: total,
        currency: CURRENCY,
        order_id: orderId ? String(orderId) : undefined,
        ...toMetaContents(items),
      },
      eventId,
    );
  });

/**
 * Oferta de "instalar o app" (containers/InstallAppCard.js). `method` é
 * "prompt" (Android) ou "ios" (instrução manual). No iPhone não há como saber
 * se a pessoa adicionou de fato: só o Android informa o resultado.
 */
export const trackInstallOffer = (method) =>
  track(() => {
    pushDataLayer("app_install_offer", { install_method: method });
  });

export const trackInstallResult = (method, outcome) =>
  track(() => {
    pushDataLayer("app_install", {
      install_method: method,
      install_outcome: outcome,
    });
  });
